import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Row } from '../Row';
import ActiveIcon from './active.svg';
import InactiveIcon from './inactive.svg';

interface IProps {
  /** The text label to display for this step */
  title: string;
  /**
   * Whether this step is currently active.
   * Active steps use the active icon and neutral_600 text color.
   * @default false
   */
  isActive?: boolean;
  /**
   * Whether this step has been completed.
   * Completed steps use the active icon and neutral_600 text color.
   * @default false
   */
  isCompleted?: boolean;
}

/**
 * A single step item component used within the Stepper.
 * Displays a step with an icon indicator and title text.
 *
 * @example
 * ```tsx
 * <Item
 *   title="Personal Information"
 *   isActive={true}
 *   isCompleted={false}
 * />
 * ```
 *
 * @param props - The step item component props
 * @returns A step item with icon and title
 */
export const Item = ({ title, isActive = false, isCompleted = false }: IProps) => {
  const getIconSrc = () => {
    if (isActive || isCompleted) return ActiveIcon;

    return InactiveIcon;
  };

  const getTextColor = () => {
    if (isActive || isCompleted) return colors.neutral[600];

    return colors.neutral[300];
  };

  return (
    <Row alignItems="center" data-testid={`steps.item-${title}`} gap={8}>
      <img alt="step-indicators" src={getIconSrc()} />
      <Typography as="span" color={getTextColor()}>{title}</Typography>
    </Row>
  );
};
